/**
 * @lodex-pro/web-shared/auth/forgot-password — self-service password reset.
 *
 * Quick 260827-pwd. Two stages: request a code, then submit the code with a new
 * password. Talks to Cognito DIRECTLY via Amplify's `resetPassword` /
 * `confirmResetPassword` — deliberately NOT through our API.
 *
 * WHY NO API ENDPOINT (the security design, approved 2026-08-27):
 *
 *   `resolve-driver-username` had to hand-build enumeration safety — returning a
 *   deterministic fake sub on a miss — because it queries OUR Postgres and nothing
 *   else guaranteed a uniform response. That precedent's PRINCIPLE applies here;
 *   its mechanism does not need rebuilding, because Cognito already provides it.
 *   The app clients are created with `preventUserExistenceErrors: true`
 *   (infrastructure/aws-cdk/lib/cognito-stack.ts), so ForgotPassword returns a
 *   generic CodeDeliveryDetails whether or not the account exists. Proxying through
 *   our API to fake that would duplicate a guarantee we already hold AND add a
 *   pre-auth public endpoint as new attack surface.
 *
 *   That setting is therefore SECURITY-CRITICAL to this flow. A CDK assertion pins
 *   it so it cannot be turned off silently.
 *
 * TWO ACCEPTED TRADEOFFS, documented rather than hidden:
 *   1. Rate limiting is Cognito's own, not our publicEndpointProtection limiter —
 *      the browser never touches our API, so ours cannot apply. Regaining it would
 *      mean proxying, which reintroduces the endpoint we just argued against.
 *   2. The code email is Cognito-branded (no-reply@verificationemail.com), a
 *      deviation from DEC-185's suppress-and-send-our-own posture. It is also the
 *      reason this WORKS today: Cognito's mailer is not SES, so it reaches real
 *      inboxes despite the SES sandbox (OPEN-019). An SES-based flow would be
 *      blocked until production access clears. Revisit branding after that.
 *
 * NOT offered in web-admin. Platform admins are Paelode-provisioned and
 * ops-reset; a self-service reset there means anyone controlling that inbox can
 * take the account. That exclusion is pre-existing and deliberate.
 */

import { useState } from 'react';
import type { FC } from 'react';
import { useForm, type SubmitHandler } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { resetPassword, confirmResetPassword } from 'aws-amplify/auth';
import { getErrorName, mapAmplifyError } from './error-mapper.js';
import { PasswordField } from './password-field.js';
import { newPasswordPairSchema, PASSWORD_RULES_TEXT } from './password-rules.js';

const requestSchema = z.object({
  email: z
    .string()
    .trim()
    .min(1, 'Enter your email address.')
    .email('Enter a valid email address.'),
});
type RequestForm = z.infer<typeof requestSchema>;

const confirmSchema = z
  .object({ code: z.string().trim().min(1, 'Enter the code from your email.') })
  .and(newPasswordPairSchema);
type ConfirmForm = z.infer<typeof confirmSchema>;

export interface ForgotPasswordProps {
  /** Back to the sign-in form. */
  readonly onCancel: () => void;
  /** Fired once the password has been reset, so the caller can return to sign-in. */
  readonly onReset: () => void;
}

export const ForgotPassword: FC<ForgotPasswordProps> = ({ onCancel, onReset }) => {
  const [stage, setStage] = useState<'request' | 'confirm'>('request');
  const [email, setEmail] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const requestForm = useForm<RequestForm>({ resolver: zodResolver(requestSchema) });
  const confirmForm = useForm<ConfirmForm>({ resolver: zodResolver(confirmSchema) });

  const onRequest: SubmitHandler<RequestForm> = async (values) => {
    setErrorMessage(null);
    try {
      await resetPassword({ username: values.email });
    } catch (err: unknown) {
      // Deliberately NOT surfaced as "no such account". With
      // preventUserExistenceErrors enabled Cognito does not tell us that anyway,
      // and echoing an error here would re-open the enumeration channel this
      // design closes. Throttling IS surfaced, because it is about the requester
      // rather than the account and leaves the user stuck otherwise. Everything
      // else advances to the code stage, where a wrong or absent code fails
      // uniformly.
      const name = getErrorName(err);
      if (name === 'LimitExceededException' || name === 'TooManyRequestsException') {
        setErrorMessage('Too many attempts. Wait a few minutes and try again.');
        return;
      }
    }
    setEmail(values.email);
    setStage('confirm');
  };

  const onConfirm: SubmitHandler<ConfirmForm> = async (values) => {
    setErrorMessage(null);
    try {
      await confirmResetPassword({
        username: email,
        confirmationCode: values.code,
        newPassword: values.newPassword,
      });
      onReset();
    } catch (err: unknown) {
      const name = getErrorName(err);
      setErrorMessage(
        name === 'CodeMismatchException' || name === 'ExpiredCodeException'
          ? 'That code is not valid or has expired. Request a new one.'
          : name === 'LimitExceededException'
            ? 'Too many attempts. Wait a few minutes and try again.'
            : mapAmplifyError(err).userMessage,
      );
    }
  };

  if (stage === 'request') {
    return (
      <form
        // Quick 260827-pwd — the key is load-bearing, not decoration. Both stages
        // render <form><p><label><input>, so without distinct keys React reconciles
        // them as the SAME element and reuses the input DOM node: the email the user
        // just typed reappears inside the code field, and gets submitted as the
        // confirmation code. Caught by test, not by reading it.
        key="request"
        onSubmit={(e) => {
          void requestForm.handleSubmit(onRequest)(e);
        }}
        aria-label="Reset your password"
      >
        <p>Enter your email address and we will send you a code to reset your password.</p>
        <label htmlFor="fp-email">Email</label>
        <input
          id="fp-email"
          type="email"
          autoComplete="username"
          {...requestForm.register('email')}
        />
        {requestForm.formState.errors.email !== undefined && (
          <p role="alert">{requestForm.formState.errors.email.message}</p>
        )}

        <button type="submit" disabled={requestForm.formState.isSubmitting}>
          {requestForm.formState.isSubmitting ? 'Sending...' : 'Send code'}
        </button>
        <button type="button" className="auth-link" onClick={onCancel}>
          Back to sign in
        </button>

        {errorMessage !== null && <p role="alert">{errorMessage}</p>}
      </form>
    );
  }

  return (
    <form
      // See the note on the request form above — distinct keys force a remount.
      key="confirm"
      onSubmit={(e) => {
        void confirmForm.handleSubmit(onConfirm)(e);
      }}
      aria-label="Enter your reset code"
    >
      {/* Intentionally neutral: says a code was sent IF the account exists, so the
          screen itself does not confirm existence either. */}
      <p>If an account exists for {email}, a code has been sent to it. Enter it below.</p>

      <label htmlFor="fp-code">Code</label>
      <input
        className="keep-case"
        id="fp-code"
        type="text"
        inputMode="numeric"
        autoComplete="one-time-code"
        {...confirmForm.register('code')}
      />
      {confirmForm.formState.errors.code !== undefined && (
        <p role="alert">{confirmForm.formState.errors.code.message}</p>
      )}

      <PasswordField
        label="New password"
        id="fp-new-password"
        autoComplete="new-password"
        hint={PASSWORD_RULES_TEXT}
        error={confirmForm.formState.errors.newPassword?.message}
        {...confirmForm.register('newPassword')}
      />
      <PasswordField
        label="Confirm new password"
        id="fp-confirm-password"
        autoComplete="new-password"
        error={confirmForm.formState.errors.confirmNewPassword?.message}
        {...confirmForm.register('confirmNewPassword')}
      />

      <button type="submit" disabled={confirmForm.formState.isSubmitting}>
        {confirmForm.formState.isSubmitting ? 'Resetting...' : 'Reset password'}
      </button>
      <button type="button" className="auth-link" onClick={onCancel}>
        Back to sign in
      </button>

      {errorMessage !== null && <p role="alert">{errorMessage}</p>}
    </form>
  );
};
